// Employer portal — register a company, post a hourly job. Highlighted in orange on the map.
import React, { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { Building2, Star, Send, X, MapPin, Trash2, CheckCircle2, Clock3, XCircle, Sparkles, Eye, Bookmark, TrendingUp } from "lucide-react";
import { CATEGORIES, categoryFor } from "../lib/categories";
import { CategoryIcon } from "../lib/categoryIcons";
import { useAuth } from "../lib/auth";
import { fetchEmployerAnalytics } from "../lib/employerApi";
import GoogleButton from "./GoogleButton";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const STATUS_STYLE = {
    approved: { bg: "bg-emerald-50", text: "text-emerald-700", icon: CheckCircle2, label: "Live" },
    pending: { bg: "bg-amber-50", text: "text-amber-700", icon: Clock3, label: "Pending review" },
    rejected: { bg: "bg-red-50", text: "text-red-700", icon: XCircle, label: "Rejected" },
};

export default function EmployerPortal({ onClose, onToast }) {
    const { user, loading: authLoading, login } = useAuth();
    const [employer, setEmployer] = useState(null);
    const [jobs, setJobs] = useState([]);
    const [loading, setLoading] = useState(true);

    // Registration form
    const [companyName, setCompanyName] = useState("");
    const [companyWebsite, setCompanyWebsite] = useState("");
    const [regSubmitting, setRegSubmitting] = useState(false);
    const [regErr, setRegErr] = useState("");

    // Post-job form
    const [showPost, setShowPost] = useState(false);
    const [postForm, setPostForm] = useState({
        title: "", cat: "warehouse", city: "", state: "",
        pay: "", shift: "", url: "", description: "",
        lat: "", lng: "",
    });
    const [locating, setLocating] = useState(false);
    const [postSubmitting, setPostSubmitting] = useState(false);
    const [postErr, setPostErr] = useState("");
    // Engagement stats returned by /api/employer/analytics.
    const [analytics, setAnalytics] = useState(null);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const me = await axios.get(`${API}/employer/me`);
            setEmployer(me.data.employer || null);
            if (me.data.employer) {
                const j = await axios.get(`${API}/employer/jobs`);
                setJobs(j.data.jobs || []);
                try {
                    const a = await fetchEmployerAnalytics();
                    setAnalytics(a);
                } catch { setAnalytics(null); }
            }
        } catch (e) {
            // 401 handled by user check below
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        if (authLoading) return;
        if (!user) {
            setLoading(false);
            return;
        }
        load();
    }, [user, authLoading, load]);

    const handleRegister = async (e) => {
        e.preventDefault();
        setRegErr("");
        if (companyName.trim().length < 2) return setRegErr("Enter your company name.");
        if (!companyWebsite.trim().includes(".")) return setRegErr("Enter your company website.");
        setRegSubmitting(true);
        try {
            const r = await axios.post(`${API}/employer/register`, {
                company_name: companyName.trim(),
                company_website: companyWebsite.trim(),
            });
            setEmployer(r.data.employer);
            onToast && onToast(r.data.auto_verified ? "You're verified! Post your first job." : "Registration submitted — pending admin review.");
        } catch (e) {
            setRegErr(e?.response?.data?.detail || "Couldn't register — try again.");
        } finally {
            setRegSubmitting(false);
        }
    };

    const geocodeCity = async () => {
        if (!postForm.city.trim()) {
            setPostErr("Enter a city first, then click Locate.");
            return;
        }
        setLocating(true);
        setPostErr("");
        try {
            const r = await axios.get(`${API}/geocode`, {
                params: { q: `${postForm.city.trim()}${postForm.state ? ", " + postForm.state : ""}` },
            });
            const first = (r.data.results || [])[0];
            if (!first) {
                setPostErr("Couldn't find that city in USA / Canada.");
            } else {
                setPostForm((f) => ({ ...f, lat: first.lat, lng: first.lng }));
            }
        } catch {
            setPostErr("Geocoding failed — please try a different city.");
        } finally {
            setLocating(false);
        }
    };

    const handlePost = async (e) => {
        e.preventDefault();
        setPostErr("");
        if (postForm.title.trim().length < 2) return setPostErr("Enter a job title.");
        if (postForm.city.trim().length < 2) return setPostErr("Enter the city.");
        if (!postForm.url.trim().includes(".")) return setPostErr("Enter a valid apply URL (link to your job page).");
        if (!postForm.lat || !postForm.lng) return setPostErr("Click 'Locate on map' to fix the position.");
        setPostSubmitting(true);
        try {
            await axios.post(`${API}/employer/jobs`, {
                ...postForm,
                lat: Number(postForm.lat),
                lng: Number(postForm.lng),
            });
            onToast && onToast(employer?.admin_approved ? "Job posted — now live on the map ✨" : "Job posted — awaiting admin approval.");
            setShowPost(false);
            setPostForm({
                title: "", cat: "warehouse", city: "", state: "",
                pay: "", shift: "", url: "", description: "",
                lat: "", lng: "",
            });
            await load();
        } catch (e) {
            setPostErr(e?.response?.data?.detail || "Failed to post job.");
        } finally {
            setPostSubmitting(false);
        }
    };

    const handleDelete = async (jobId) => {
        if (!window.confirm("Delete this job posting?")) return;
        try {
            await axios.delete(`${API}/employer/jobs/${jobId}`);
            setJobs((js) => js.filter((j) => j.job_id !== jobId));
            onToast && onToast("Job removed.");
        } catch {
            onToast && onToast("Couldn't delete just now.");
        }
    };

    // Not signed in
    if (!authLoading && !user) {
        return (
            <div className="fixed inset-0 z-[600] bg-white flex items-center justify-center p-6" data-testid="employer-gate">
                <div className="max-w-md w-full text-center">
                    <div className="w-14 h-14 mx-auto rounded-2xl bg-[#ffdccb] text-[#e85812] flex items-center justify-center mb-4">
                        <Building2 className="w-7 h-7" />
                    </div>
                    <h1 className="font-display font-extrabold text-2xl text-[#1a1e2c] mb-2">
                        Post staffing jobs on Dailyworkjobs
                    </h1>
                    <p className="text-slate-500 font-body text-[14px] mb-2">
                        Sign in with the email on your company domain to auto-verify and post jobs instantly.
                    </p>
                    <p className="text-slate-400 font-body text-[13px] mb-5">
                        Free-mail domains (gmail, yahoo, etc.) can still register — an admin will approve within 24 hrs.
                    </p>
                    <GoogleButton onClick={login} label="Continue with Google" />
                    <button className="mt-4 text-sm text-slate-500 hover:text-[#e85812] underline" onClick={onClose}>
                        Back to jobs
                    </button>
                </div>
            </div>
        );
    }

    return (
        <div className="fixed inset-0 z-[600] bg-white overflow-auto" data-testid="employer-portal">
            <div className="max-w-4xl mx-auto p-5 md:p-8">
                <div className="flex items-center justify-between mb-5 flex-wrap gap-3">
                    <div>
                        <div className="inline-flex items-center gap-2 bg-[#fff2ea] text-[#e85812] px-3 py-1 rounded-full text-[11px] font-display font-bold uppercase tracking-wide">
                            <Star className="w-3.5 h-3.5 fill-[#e85812]" /> Employer portal
                        </div>
                        <h1 className="font-display font-extrabold text-2xl md:text-3xl text-[#1a1e2c] mt-2">
                            Post daily-wage jobs to thousands of nearby workers
                        </h1>
                        <div className="text-sm text-slate-500 mt-1">Signed in as <b>{user?.email}</b></div>
                    </div>
                    <button className="btn-ghost" onClick={onClose} data-testid="employer-close">
                        <X className="w-4 h-4" /> Close
                    </button>
                </div>

                {loading && <div className="text-slate-500">Loading…</div>}

                {!loading && !employer && (
                    <form onSubmit={handleRegister} className="card-soft p-6 space-y-4" data-testid="employer-register-form">
                        <div className="flex items-center gap-2">
                            <Sparkles className="w-4 h-4 text-[#e85812]" />
                            <div className="font-display font-bold text-lg text-[#1a1e2c]">Register your company</div>
                        </div>
                        <p className="text-[13.5px] text-slate-600 -mt-2">
                            We auto-verify if your Google login domain matches the company website domain. Otherwise our admin will review within 24 hours.
                        </p>
                        <div className="m3-field">
                            <input
                                id="er-name"
                                type="text"
                                placeholder=" "
                                value={companyName}
                                onChange={(e) => setCompanyName(e.target.value)}
                                data-testid="employer-company-name"
                            />
                            <label htmlFor="er-name">Company name</label>
                        </div>
                        <div className="m3-field">
                            <input
                                id="er-web"
                                type="url"
                                placeholder=" "
                                value={companyWebsite}
                                onChange={(e) => setCompanyWebsite(e.target.value)}
                                data-testid="employer-company-website"
                            />
                            <label htmlFor="er-web">Company website (e.g. https://acme.com)</label>
                        </div>
                        {regErr && <div className="text-[13px] text-red-600" data-testid="employer-reg-err">{regErr}</div>}
                        <button
                            type="submit"
                            disabled={regSubmitting}
                            className="btn-primary !py-3 w-full !text-[15px]"
                            data-testid="employer-register-submit"
                        >
                            {regSubmitting ? "Registering…" : "Register"}
                        </button>
                    </form>
                )}

                {!loading && employer && (
                    <>
                        <div
                            className={`card-soft p-5 mb-5 flex items-center justify-between flex-wrap gap-3 ${
                                employer.admin_approved ? "border-l-4 border-emerald-500" : "border-l-4 border-amber-500"
                            }`}
                            data-testid="employer-status-card"
                        >
                            <div>
                                <div className="font-display font-bold text-[17px] text-[#1a1e2c]">{employer.company_name}</div>
                                <div className="text-sm text-slate-500">{employer.company_domain}</div>
                                <div className="mt-2 text-[13px]">
                                    {employer.admin_approved ? (
                                        <span className="inline-flex items-center gap-1.5 text-emerald-700 bg-emerald-50 px-2.5 py-1 rounded-full font-display font-semibold">
                                            <CheckCircle2 className="w-4 h-4" /> Verified — post jobs instantly
                                        </span>
                                    ) : (
                                        <span className="inline-flex items-center gap-1.5 text-amber-700 bg-amber-50 px-2.5 py-1 rounded-full font-display font-semibold">
                                            <Clock3 className="w-4 h-4" /> Pending admin approval (usually &lt; 24 hrs)
                                        </span>
                                    )}
                                </div>
                            </div>
                            <button
                                className="btn-primary"
                                onClick={() => setShowPost((v) => !v)}
                                data-testid="employer-post-toggle"
                            >
                                <Star className="w-4 h-4 fill-white" /> {showPost ? "Cancel" : "Post a job"}
                            </button>
                        </div>

                        {/* Feature-your-job upsell — email-based, no payment for now. */}
                        <div
                            className="card-soft p-5 mb-5 border border-[#ffe08a] bg-gradient-to-br from-[#fff9dd] to-white"
                            data-testid="employer-featured-cta"
                        >
                            <div className="flex items-start gap-3">
                                <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-[#fff3b8] to-[#f5c542] flex items-center justify-center shrink-0 shadow-sm">
                                    <Star className="w-5 h-5 text-[#7a5806] fill-[#7a5806]" />
                                </div>
                                <div className="flex-1 min-w-0">
                                    <div className="font-display font-bold text-[15px] text-[#1a1e2c]">
                                        Want a golden pin? Get your job featured.
                                    </div>
                                    <p className="text-[13px] text-slate-600 mt-1 leading-relaxed">
                                        Featured jobs show up first with a distinct <b>gold pin + ★</b> across the map and daily
                                        alerts — perfect for urgent hires. It&apos;s free while we roll it out.
                                    </p>
                                    <a
                                        href="mailto:Hello@downtownmango.com?subject=Feature%20my%20job%20on%20Dailyworkjobs&body=Hi%20team%2C%0A%0AI%27d%20like%20to%20feature%20my%20job%20posting%3A%0A%0AJob%20title%3A%20%0ACompany%3A%20%0AJob%20URL%20on%20Dailyworkjobs%3A%20%0A%0AThanks%21"
                                        className="inline-flex items-center gap-1.5 mt-3 text-[13px] font-display font-semibold text-[#7a5806] bg-[#fff3b8] hover:bg-[#f5e29a] px-3.5 py-1.5 rounded-full transition-colors"
                                        data-testid="employer-feature-request-btn"
                                    >
                                        <Send className="w-3.5 h-3.5" /> Email Hello@downtownmango.com
                                    </a>
                                </div>
                            </div>
                        </div>

                        {showPost && (
                            <form onSubmit={handlePost} className="card-soft p-6 mb-5 space-y-3" data-testid="employer-post-form">
                                <div className="font-display font-bold text-lg text-[#1a1e2c] mb-2">New job posting</div>
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                                    <div className="m3-field md:col-span-2">
                                        <input
                                            id="pf-title"
                                            type="text"
                                            placeholder=" "
                                            value={postForm.title}
                                            onChange={(e) => setPostForm({ ...postForm, title: e.target.value })}
                                            data-testid="post-title-input"
                                        />
                                        <label htmlFor="pf-title">Job title (e.g. Warehouse Associate, Line Cook)</label>
                                    </div>
                                    <div className="m3-field md:col-span-2">
                                        <label className="block text-[12px] font-display font-semibold text-slate-600 mb-1 !static !bg-transparent !p-0">Category</label>
                                        <div className="flex flex-wrap gap-2">
                                            {CATEGORIES.map((c) => (
                                                <button
                                                    key={c.id}
                                                    type="button"
                                                    onClick={() => setPostForm({ ...postForm, cat: c.id })}
                                                    className={`px-3 py-1.5 rounded-full text-[12.5px] font-display font-semibold inline-flex items-center gap-1.5 transition-all ${
                                                        postForm.cat === c.id
                                                            ? "bg-[#e85812] text-white shadow-lg"
                                                            : "bg-slate-100 text-slate-600 hover:bg-slate-200"
                                                    }`}
                                                    data-testid={`post-cat-${c.id}`}
                                                >
                                                    <CategoryIcon catId={c.id} size={14} />
                                                    {c.label}
                                                </button>
                                            ))}
                                        </div>
                                    </div>
                                    <div className="m3-field">
                                        <input
                                            id="pf-city"
                                            type="text"
                                            placeholder=" "
                                            value={postForm.city}
                                            onChange={(e) => setPostForm({ ...postForm, city: e.target.value, lat: "", lng: "" })}
                                            data-testid="post-city-input"
                                        />
                                        <label htmlFor="pf-city">City</label>
                                    </div>
                                    <div className="m3-field">
                                        <input
                                            id="pf-state"
                                            type="text"
                                            placeholder=" "
                                            value={postForm.state}
                                            onChange={(e) => setPostForm({ ...postForm, state: e.target.value, lat: "", lng: "" })}
                                            data-testid="post-state-input"
                                        />
                                        <label htmlFor="pf-state">State / Province (e.g. TX, ON)</label>
                                    </div>
                                    <div className="m3-field">
                                        <input
                                            id="pf-pay"
                                            type="text"
                                            placeholder=" "
                                            value={postForm.pay}
                                            onChange={(e) => setPostForm({ ...postForm, pay: e.target.value })}
                                            data-testid="post-pay-input"
                                        />
                                        <label htmlFor="pf-pay">Pay (e.g. $18/hr)</label>
                                    </div>
                                    <div className="m3-field">
                                        <input
                                            id="pf-shift"
                                            type="text"
                                            placeholder=" "
                                            value={postForm.shift}
                                            onChange={(e) => setPostForm({ ...postForm, shift: e.target.value })}
                                            data-testid="post-shift-input"
                                        />
                                        <label htmlFor="pf-shift">Shift (e.g. Full-time, Nights)</label>
                                    </div>
                                    <div className="m3-field md:col-span-2">
                                        <input
                                            id="pf-url"
                                            type="url"
                                            placeholder=" "
                                            value={postForm.url}
                                            onChange={(e) => setPostForm({ ...postForm, url: e.target.value })}
                                            data-testid="post-url-input"
                                        />
                                        <label htmlFor="pf-url">Apply URL (link to your careers page / ATS)</label>
                                    </div>
                                    <div className="m3-field md:col-span-2">
                                        <textarea
                                            id="pf-desc"
                                            placeholder=" "
                                            rows={3}
                                            value={postForm.description}
                                            onChange={(e) => setPostForm({ ...postForm, description: e.target.value })}
                                            data-testid="post-desc-input"
                                        />
                                        <label htmlFor="pf-desc">Short description (optional)</label>
                                    </div>
                                </div>

                                <div className="flex items-center gap-3 flex-wrap bg-slate-50 rounded-2xl p-3">
                                    <button
                                        type="button"
                                        onClick={geocodeCity}
                                        disabled={locating}
                                        className="btn-ghost !py-2 !px-3"
                                        data-testid="post-locate-btn"
                                    >
                                        <MapPin className="w-4 h-4" /> {locating ? "Finding…" : "Locate on map"}
                                    </button>
                                    <div className="text-[12.5px] text-slate-600">
                                        {postForm.lat && postForm.lng ? (
                                            <span className="text-emerald-700">✓ Located: {Number(postForm.lat).toFixed(4)}, {Number(postForm.lng).toFixed(4)}</span>
                                        ) : (
                                            <span>Location not set — click &quot;Locate on map&quot;.</span>
                                        )}
                                    </div>
                                </div>

                                {postErr && <div className="text-[13px] text-red-600" data-testid="post-error">{postErr}</div>}
                                <button
                                    type="submit"
                                    disabled={postSubmitting}
                                    className="btn-primary w-full !py-3 !text-[15px]"
                                    data-testid="post-submit-btn"
                                >
                                    <Send className="w-4 h-4" />
                                    {postSubmitting ? "Posting…" : "Post job"}
                                </button>
                            </form>
                        )}

                        <div className="mb-2 flex items-center justify-between">
                            <div className="font-display font-bold text-lg text-[#1a1e2c]">Your postings ({jobs.length})</div>
                        </div>

                        {/* Analytics dashboard — views / saves / applies per posting. */}
                        {analytics && jobs.length > 0 && (
                            <div className="card-soft p-5 mb-4" data-testid="employer-analytics">
                                <div className="flex items-center gap-2 mb-3">
                                    <div className="w-8 h-8 rounded-xl bg-[#fff2ea] flex items-center justify-center">
                                        <TrendingUp className="w-4 h-4 text-[#e85812]" />
                                    </div>
                                    <div className="font-display font-bold text-[15px] text-[#1a1e2c]">Engagement overview</div>
                                </div>
                                <div className="grid grid-cols-3 gap-2 md:gap-3 mb-4">
                                    <div className="rounded-2xl bg-gradient-to-br from-[#fff2ea] to-white border border-[#ffdccb] p-3">
                                        <div className="flex items-center gap-1.5 text-[11px] font-display font-bold uppercase tracking-wide text-[#e85812]">
                                            <Eye className="w-3.5 h-3.5" /> Views
                                        </div>
                                        <div className="font-display font-extrabold text-[22px] md:text-[26px] text-[#1a1e2c] mt-1 tabular-nums" data-testid="employer-analytics-views">
                                            {analytics.totals.views}
                                        </div>
                                    </div>
                                    <div className="rounded-2xl bg-gradient-to-br from-[#eef4ff] to-white border border-[#c9d8f2] p-3">
                                        <div className="flex items-center gap-1.5 text-[11px] font-display font-bold uppercase tracking-wide text-[#3a6bd6]">
                                            <Bookmark className="w-3.5 h-3.5" /> Saves
                                        </div>
                                        <div className="font-display font-extrabold text-[22px] md:text-[26px] text-[#1a1e2c] mt-1 tabular-nums" data-testid="employer-analytics-saves">
                                            {analytics.totals.saves}
                                        </div>
                                    </div>
                                    <div className="rounded-2xl bg-gradient-to-br from-[#e6f7ee] to-white border border-[#a8dfc0] p-3">
                                        <div className="flex items-center gap-1.5 text-[11px] font-display font-bold uppercase tracking-wide text-emerald-700">
                                            <Send className="w-3.5 h-3.5" /> Applies
                                        </div>
                                        <div className="font-display font-extrabold text-[22px] md:text-[26px] text-[#1a1e2c] mt-1 tabular-nums" data-testid="employer-analytics-applies">
                                            {analytics.totals.applies}
                                        </div>
                                    </div>
                                </div>
                                <div className="overflow-x-auto">
                                    <table className="w-full text-[13px]" data-testid="employer-analytics-table">
                                        <thead>
                                            <tr className="text-left text-[11px] font-display font-bold uppercase tracking-wide text-slate-500 border-b border-slate-100">
                                                <th className="py-2 pr-3">Job</th>
                                                <th className="py-2 px-2 text-right">Views</th>
                                                <th className="py-2 px-2 text-right">Saves</th>
                                                <th className="py-2 px-2 text-right">Applies</th>
                                                <th className="py-2 pl-2 text-right">Apply rate</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {(analytics.jobs || []).map((row) => (
                                                <tr key={row.job_id} className="border-b border-slate-50 last:border-b-0" data-testid={`employer-analytics-row-${row.job_id}`}>
                                                    <td className="py-2 pr-3">
                                                        <div className="font-display font-semibold text-[#1a1e2c] truncate max-w-[220px]">{row.title}</div>
                                                        <div className="text-[11.5px] text-slate-500">
                                                            {row.city}{row.state ? `, ${row.state}` : ""}
                                                            {row.featured && (
                                                                <span className="ml-1.5 text-[10px] font-bold uppercase tracking-wide bg-gradient-to-r from-[#fff3b8] to-[#f5c542] text-[#7a5806] px-1.5 py-0.5 rounded">★ Featured</span>
                                                            )}
                                                        </div>
                                                    </td>
                                                    <td className="py-2 px-2 text-right tabular-nums font-semibold text-[#1a1e2c]">{row.views}</td>
                                                    <td className="py-2 px-2 text-right tabular-nums text-[#3a6bd6]">{row.saves}</td>
                                                    <td className="py-2 px-2 text-right tabular-nums text-emerald-700">{row.applies}</td>
                                                    <td className="py-2 pl-2 text-right tabular-nums text-slate-600">{row.apply_rate ? `${row.apply_rate}%` : "—"}</td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                                <div className="text-[11.5px] text-slate-400 mt-3">
                                    Views count once per visitor per job every 10 minutes. Saves and applies count each unique action.
                                </div>
                            </div>
                        )}

                        <div className="space-y-2">
                            {jobs.length === 0 && (
                                <div className="card-soft p-6 text-center text-slate-500 text-sm">
                                    No jobs posted yet. Click <b>Post a job</b> to add your first opening.
                                </div>
                            )}
                            {jobs.map((j) => {
                                const cat = categoryFor(j.cat);
                                const S = STATUS_STYLE[j.status] || STATUS_STYLE.pending;
                                const Icon = S.icon;
                                return (
                                    <div
                                        key={j.job_id}
                                        className="card-soft p-4 flex items-center gap-3"
                                        data-testid={`employer-job-${j.job_id}`}
                                    >
                                        <span
                                            className="w-11 h-11 rounded-2xl flex items-center justify-center shrink-0"
                                            style={{ background: cat.color }}
                                        >
                                            <CategoryIcon catId={cat.id} size={20} />
                                        </span>
                                        <div className="flex-1 min-w-0">
                                            <div className="flex items-center gap-2 flex-wrap">
                                                <div className="font-display font-semibold text-[15px] text-[#1a1e2c] truncate">{j.title}</div>
                                                <span className={`inline-flex items-center gap-1 text-[11px] font-display font-bold px-2 py-0.5 rounded-full ${S.bg} ${S.text}`}>
                                                    <Icon className="w-3 h-3" /> {S.label}
                                                </span>
                                            </div>
                                            <div className="text-[12.5px] text-slate-500">
                                                {j.loc || j.city} {j.pay ? ` · ${j.pay}` : ""} {j.shift ? ` · ${j.shift}` : ""}
                                            </div>
                                        </div>
                                        <button
                                            className="btn-ghost !py-2 !px-2 text-red-500"
                                            onClick={() => handleDelete(j.job_id)}
                                            data-testid={`employer-job-delete-${j.job_id}`}
                                        >
                                            <Trash2 className="w-4 h-4" />
                                        </button>
                                    </div>
                                );
                            })}
                        </div>
                    </>
                )}
            </div>
        </div>
    );
}
